import { type FC, type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from "react";
import { Flipper, Flipped } from "react-flip-toolkit";
import { useUnit } from "effector-react";
import cx from "classnames";
import type { Sight, SightId } from "../../types";
import { UnstyledButton } from "../UnstyledButton";
import { $locale } from "../../state/locale";
import { LOCALE_TEXT_DIRECTION } from "../../shared/locale";
import styles from "./PoiChips.module.css";

export type NextSceneSnapshot = {
  /** id для передачи в `onNextSceneSelect` (как ожидает `handleEnterScene`). */
  sceneId: string;
  /** Готовый текст кнопки (i18n уже применён, имя локации подставлено). */
  label: string;
  /** Путь к PNG-иконке или `null`. */
  iconSrc: string | null;
};

type Props = {
  unvisitedSights: Sight[];
  visitedSights: Sight[];
  activeSightId: SightId | null;
  onSelect: (id: SightId) => void;
  nextScene?: NextSceneSnapshot | null;
  onNextSceneSelect?: (sceneId: string) => void;
};

const DIVIDER_FLIP_ID = "__divider__";
const NEXT_SCENE_FLIP_ID = "__next-scene__";
const NEXT_SCENE_DIVIDER_FLIP_ID = "__next-scene-divider__";
const DRAG_THRESHOLD_PX = 5;

export const PoiChips: FC<Props> = ({
  unvisitedSights,
  visitedSights,
  activeSightId,
  onSelect,
  nextScene,
  onNextSceneSelect,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startScrollLeft: number } | null>(null);
  const wasDraggedRef = useRef(false);
  const locale = useUnit($locale);
  const isRtl = LOCALE_TEXT_DIRECTION[locale] === "rtl";

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const root = rootRef.current;
    if (!root) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScrollLeft: root.scrollLeft,
    };
    wasDraggedRef.current = false;
  }, []);

  const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const root = rootRef.current;
    if (!drag || !root || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    if (!wasDraggedRef.current && Math.abs(dx) > DRAG_THRESHOLD_PX) {
      wasDraggedRef.current = true;
      try { root.setPointerCapture(drag.pointerId); } catch { /* noop */ }
    }
    if (wasDraggedRef.current) {
      root.scrollLeft = drag.startScrollLeft - dx;
    }
  }, []);

  const endDrag = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const root = rootRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (root && root.hasPointerCapture(drag.pointerId)) {
      try { root.releasePointerCapture(drag.pointerId); } catch { /* noop */ }
    }
    dragRef.current = null;
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0 && e.deltaX === 0) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;
      e.preventDefault();
      root.scrollLeft += isRtl ? -delta : delta;
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [isRtl]);

  const hasNextScene = Boolean(nextScene && onNextSceneSelect);

  if (unvisitedSights.length === 0 && visitedSights.length === 0 && !hasNextScene) {
    return null;
  }

  const handleChipClick = (id: SightId) => {
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    onSelect(id);
  };

  const handleNextSceneClick = () => {
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    if (nextScene && onNextSceneSelect) {
      onNextSceneSelect(nextScene.sceneId);
    }
  };

  // flipKey должен меняться при любой реорганизации chip'ов, чтобы react-flip-toolkit
  // снова прогнал FLIP. Кодируем полную упорядоченную последовательность id —
  // любой reorder / mount / unmount триггерит анимацию. Префикс `n:` для
  // next-scene chip отделяет его от sight id'шников, чтобы не было коллизий.
  const flipKey = [
    nextScene ? `n:${nextScene.sceneId}` : "",
    nextScene ? "nd" : "",
    ...unvisitedSights.map((s) => `u:${s.id}`),
    visitedSights.length > 0 ? "d" : "",
    ...visitedSights.map((s) => `v:${s.id}`),
  ].join("|");

  const renderChip = (sight: Sight, visited: boolean) => (
    <Flipped key={String(sight.id)} flipId={String(sight.id)}>
      <UnstyledButton
        className={cx(
          styles.chip,
          sight.id === activeSightId && styles.active,
          visited && styles.visited,
        )}
        dir={isRtl ? "rtl" : undefined}
        onClick={() => handleChipClick(sight.id)}
        onMouseDown={(e) => e.preventDefault()}
        onPointerDown={(e) => e.preventDefault()}
      >
        <div className={styles.chipIconFrame}>
          {sight.icon ? (
            <img
              src={sight.icon.src}
              srcSet={sight.icon.srcSet}
              alt={sight.icon.alt ?? sight.name ?? ""}
              width={20}
              height={20}
              className={styles.chipIcon}
              loading="lazy"
            />
          ) : (
            <span className={styles.chipFallback} aria-hidden />
          )}
        </div>
        <span className={styles.chipName}>{sight.name}</span>
      </UnstyledButton>
    </Flipped>
  );

  return (
    <Flipper flipKey={flipKey} spring={{ stiffness: 220, damping: 26 }}>
      <div
        className={styles.root}
        ref={rootRef}
        dir={isRtl ? "rtl" : "ltr"}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className={styles.container}>
          {nextScene && onNextSceneSelect && (
            <>
              <Flipped key={NEXT_SCENE_FLIP_ID} flipId={NEXT_SCENE_FLIP_ID}>
                <UnstyledButton
                  className={cx(styles.chip, styles.nextSceneChip)}
                  dir={isRtl ? "rtl" : undefined}
                  onClick={handleNextSceneClick}
                  onMouseDown={(e) => e.preventDefault()}
                  onPointerDown={(e) => e.preventDefault()}
                >
                  <div className={cx(styles.chipIconFrame, styles.nextSceneIconFrame)}>
                    {nextScene.iconSrc ? (
                      <img
                        src={nextScene.iconSrc}
                        alt=""
                        width={20}
                        height={20}
                        className={styles.chipIcon}
                        loading="lazy"
                      />
                    ) : (
                      <span className={styles.chipFallback} aria-hidden />
                    )}
                  </div>
                  <span className={cx(styles.chipName, styles.nextSceneName)}>
                    {nextScene.label}
                  </span>
                </UnstyledButton>
              </Flipped>
              {(unvisitedSights.length > 0 || visitedSights.length > 0) && (
                <Flipped key={NEXT_SCENE_DIVIDER_FLIP_ID} flipId={NEXT_SCENE_DIVIDER_FLIP_ID}>
                  <span className={styles.divider} aria-hidden />
                </Flipped>
              )}
            </>
          )}
          {unvisitedSights.map((s) => renderChip(s, false))}
          {visitedSights.length > 0 && unvisitedSights.length > 0 && (
            <Flipped key={DIVIDER_FLIP_ID} flipId={DIVIDER_FLIP_ID}>
              <span className={styles.divider} aria-hidden />
            </Flipped>
          )}
          {visitedSights.map((s) => renderChip(s, true))}
        </div>
      </div>
    </Flipper>
  );
};
